import { ViewerAnnotation } from './types';

const STORAGE_KEY = 'specledger-demo-annotations';

// Demo annotations to show on first load
export const demoAnnotations: Record<string, ViewerAnnotation[]> = {
  'spec.md': [
    {
      id: 'demo-ann-1',
      type: 'comment',
      text: 'multiple authentication methods',
      comment: 'Should we prioritize OAuth over email/password for the initial release?',
      createdAt: Date.now() - 3600000, // 1 hour ago
      position: {
        blockIndex: 2,
        startOffset: 67,
        endOffset: 99,
      },
    },
    {
      id: 'demo-ann-2',
      type: 'highlight',
      text: 'Sessions expire after 7 days of inactivity',
      createdAt: Date.now() - 7200000, // 2 hours ago
      position: {
        blockIndex: 14,
        startOffset: 0,
        endOffset: 42,
      },
    },
    {
      id: 'demo-ann-3',
      type: 'comment',
      text: 'bcrypt (cost factor 12)',
      comment: 'Consider using Argon2id instead for better security against GPU attacks',
      createdAt: Date.now() - 1800000, // 30 min ago
      position: {
        blockIndex: 21,
        startOffset: 23,
        endOffset: 46,
      },
    },
  ],
  'plan.md': [
    {
      id: 'demo-ann-4',
      type: 'deletion',
      text: 'blocked',
      createdAt: Date.now() - 900000, // 15 min ago
      position: {
        blockIndex: 12,
        startOffset: 27,
        endOffset: 34,
      },
    },
    {
      id: 'demo-ann-5',
      type: 'comment',
      text: 'Short expiry limits damage from token theft',
      comment: 'Good security consideration. We should also implement token revocation.',
      createdAt: Date.now() - 5400000, // 1.5 hours ago
      position: {
        blockIndex: 17,
        startOffset: 0,
        endOffset: 43,
      },
    },
  ],
};

interface StoredAnnotations {
  [artifactName: string]: ViewerAnnotation[];
}

// Check if we're in a browser environment
function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

// Load all annotations from localStorage
export function loadAllAnnotations(): StoredAnnotations {
  if (!isBrowser()) return {};

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to load annotations from localStorage:', e);
  }

  return {};
}

// Load annotations for a specific artifact
export function loadAnnotations(artifactName: string): ViewerAnnotation[] {
  if (!isBrowser()) return demoAnnotations[artifactName] || [];

  const allAnnotations = loadAllAnnotations();

  // If no saved annotations exist, use demo annotations
  if (!allAnnotations[artifactName] && demoAnnotations[artifactName]) {
    return demoAnnotations[artifactName];
  }

  return allAnnotations[artifactName] || [];
}

// Save annotations for a specific artifact
export function saveAnnotations(artifactName: string, annotations: ViewerAnnotation[]): void {
  if (!isBrowser()) return;

  try {
    const allAnnotations = loadAllAnnotations();
    allAnnotations[artifactName] = annotations;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allAnnotations));
  } catch (e) {
    console.error('Failed to save annotations to localStorage:', e);
  }
}

// Clear all annotations (useful for resetting demo)
export function clearAllAnnotations(): void {
  if (!isBrowser()) return;

  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.error('Failed to clear annotations from localStorage:', e);
  }
}

// Check if annotations have been modified from the saved state
export function hasUnsavedChanges(artifactName: string, currentAnnotations: ViewerAnnotation[]): boolean {
  const savedAnnotations = loadAnnotations(artifactName);

  if (savedAnnotations.length !== currentAnnotations.length) {
    return true;
  }

  const savedIds = new Set(savedAnnotations.map(a => a.id));
  const currentIds = new Set(currentAnnotations.map(a => a.id));

  // Check if all IDs match
  for (const id of savedIds) {
    if (!currentIds.has(id)) return true;
  }
  for (const id of currentIds) {
    if (!savedIds.has(id)) return true;
  }

  // Check if any annotation content changed
  for (const current of currentAnnotations) {
    const saved = savedAnnotations.find(a => a.id === current.id);
    if (saved && saved.comment !== current.comment) {
      return true;
    }
  }

  return false;
}
